// docs/116 the cozy inn: what the inn's layer and its overlays draw. `Village.tsx` reads the module's
// rows (`inn_visit`, `dice_table`, `inn_song`, `inn_cheer`, `inn_guestbook`, `inn_rent`) into these
// shapes; the `?village=1` harness feeds stand-ins (a local dice table run by `dice.ts`).

/** CI3: where a band is in the inn (`village_presence.pose`). */
export const POSE = { standing: 0, table: 1, hearth: 2, armchair: 3, window: 4, dice: 5, bar: 6, asleep: 7 } as const;
export type PoseName = keyof typeof POSE;
export type PoseCode = (typeof POSE)[PoseName];

// ---- CI4/CI5 tavern dice: Hearth Bones ----------------------------------------------------------
/** The dice table's state (`dice_table.state`). */
export type DiceState = 'open' | 'playing' | 'settling';
/** A stool's state in the game being played (`DiceSeat.state`). */
export type DiceSeatState = 'empty' | 'waiting' | 'playing' | 'stood' | 'bust' | 'out';
/** What just happened at the table (`dice_table.last`): each new `throwN` or state change carries one. */
export type DiceLast = 'none' | 'thrown' | 'pushed' | 'bust' | 'stood' | 'gallows' | 'crowns' | 'hearth' | 'triple' | 'won' | 'kitty' | 'sat' | 'left' | 'started' | 'timeout';

export interface DiceSeatView {
  /** 'empty' stools are free; a 'regular' is an inn regular playing for the house. */
  kind: 'empty' | 'hero' | 'regular';
  state: DiceSeatState;
  name: string;
  /** Oryx creature frame (a hero's `oryxFrame`, or a regular's folk frame); 0 = the hero's class frame. */
  frame: number;
  classId?: string;
  heroId?: bigint;
  /** The player's identity hex (a hero's seat). */
  owner?: string;
  /** This seat is mine. */
  mine: boolean;
  /** The hand's value once stood (`dice.ts handValue`), else 0. */
  score: number;
  /** Their final faces once they have played (0 = not thrown). */
  hand: [number, number, number];
  /** A regular's temper: 0 cautious … 2 bold (drawn as a word under their name). */
  temper?: number;
  /** Gold won at the last settle (shown over the seat). */
  won?: number;
}

export interface DiceView {
  state: DiceState;
  /** 1, 3 or 5 gold a head; 0 until someone has sat down. */
  stake: number;
  pot: number;
  kitty: number;
  /** Always four stools, left to right as seen from the room. */
  seats: DiceSeatView[];
  /** The seat whose turn it is while playing (−1 none). */
  turn: number;
  /** The three dice on the table now (1–6; 0 = none thrown yet this turn). */
  dice: [number, number, number];
  /** Bit i set = die i was thrown by the latest throw (`throwN`). */
  thrown: number;
  /** Counts every throw at this table ever; a new value is a new throw to animate. */
  throwN: number;
  /** Pushes the player whose turn it is has left (2 after the first throw). */
  pushes: number;
  last: DiceLast;
  /** Server ms when the current wait ends (a choice, the next game). */
  deadlineMs: number;
  /** Counts games; a new value is a new game. */
  gameN: number;
  /** The seats that took the last pot. */
  winners: number[];
}

/** What a player can do at the table; each resolves to whether the module took it. */
export interface DiceActions {
  /** Sit `heroId` on a free stool (or a regular's, between games). */
  sit(heroId: bigint): Promise<boolean>;
  stand(): Promise<boolean>;
  /** Set the stake (first sitter, while open) and start the game. */
  start(stake: number): Promise<boolean>;
  /** Throw again the dice whose bits are set (my turn, a push left). */
  push(mask: number): Promise<boolean>;
  /** Keep the hand (my turn, after the first throw). */
  hold(): Promise<boolean>;
  /** Throw all three to begin my turn (the first throw). */
  roll(): Promise<boolean>;
  /** Called about once a second by every client in the room; the module acts only when something is due. */
  tick(): void;
}

export const DICE_STAKES = [1, 3, 5] as const;

// ---- CI7 the bard ------------------------------------------------------------------------------
/** The song the bard is playing (or will play) as `inn_song` and the schedule give it. */
export interface InnSongView {
  /** Index into `tunes.ts TUNES`. */
  tune: number;
  /** Server ms the tune began. */
  startedAtMs: number;
  /** Who asked for it ('' = the bard's own choice). */
  by: string;
}

// ---- CI6/CI12 moments everyone sees ------------------------------------------------------------
export type CheerKind = 'toast' | 'drink' | 'song';
export type DrinkKind = 'ale' | 'cider' | 'mead' | 'wine';
export const DRINKS: readonly DrinkKind[] = ['ale', 'cider', 'mead', 'wine'];
export const DRINK_PRICE: Record<DrinkKind, number> = { ale: 1, cider: 1, mead: 1, wine: 2 };
export const DRINK_NAME: Record<DrinkKind, string> = { ale: 'a mug of ale', cider: 'a cup of cider', mead: 'a horn of mead', wine: 'hot spiced wine' };
export interface InnCheer {
  /** Unique per event in this client (a counter), so an effect fires once. */
  n: number;
  kind: CheerKind;
  /** The player who raised it (identity hex). */
  owner: string;
  /** The hero (a drink's drinker), as a `VillageHero.key`. */
  heroKey?: string;
  /** A drink's kind, or a song's tune. */
  drink?: DrinkKind;
  tune?: number;
  /** Who: a profile name, for the words over heads. */
  who: string;
  /** docs/121 VW2: the module's seed, so every view picks the same voices and words. */
  seed: number;
}

/** docs/121 VW2: a moment a player started that every view in the room plays alike (`village_moment`). */
export type MomentKind = 'pet' | 'fetch';
export const MOMENTS: readonly MomentKind[] = ['pet', 'fetch'];
export interface InnMoment {
  /** Unique per event in this client, so an effect fires once. */
  n: number;
  kind: MomentKind;
  /** The hero who pets or throws, as a `VillageHero.key`. */
  heroKey: string;
  /** Where they stand (cells). */
  x: number;
  y: number;
  seed: number;
  /** When it happened (server ms). */
  atMs: number;
  mine: boolean;
}

// ---- CI11 the guestbook --------------------------------------------------------------------------
export interface GuestbookEntry { id: bigint; heroName: string; who: string; text: string; atMs: number; mine: boolean }
export const GUESTBOOK_MAX = 160;

// ---- docs/121 VW4 players' notes on the noticeboard ---------------------------------------------------
/** The papers a player can pin a note on, in `inn_notice.paper` order. */
export const NOTE_PAPERS = ['parchment', 'scrap', 'wanted', 'bill', 'card'] as const;
export type NotePaper = (typeof NOTE_PAPERS)[number];
export interface PlayerNotice { id: bigint; heroName: string; who: string; title: string; text: string; paper: NotePaper; atMs: number; mine: boolean }
/** `inn.rs` NOTICE_TITLE_CHARS / NOTICE_MAX_CHARS / NOTICE_MINE. */
export const NOTICE_TITLE_MAX = 40;
export const NOTICE_MAX = 200;
export const NOTICE_MINE = 3;

// ---- CI9/CI10 the storyteller's tales and the noticeboard's notices ---------------------------------
/** Something the World's players did, for the storyteller (CI9). */
export interface Deed {
  /** The storyteller's own line (a chronicle voice), or one made from a defeated foe / settled quest. */
  text: string;
  kind: 'chronicle' | 'foe' | 'quest';
  /** The foe's or the quest's name, the place. */
  name?: string;
  place?: string;
  heroes?: string[];
  atMs: number;
}
/** A side quest on the board, for a notice's adventure hook (CI10); docs/121 VW5: its offer, to take it up. */
export interface Hook { title: string; asker: string; pitch: string; location: string; offerId?: bigint }
/** A villager's line the noticeboard may pin as a rumour (CI10). */
export interface Rumour { who: string; text: string }

/** Everything the inn's layer reads besides the room and the people. */
export interface InnData {
  /** CI1: `inn_visit.seed` (0 = none yet: the harness, or a row not arrived). */
  visitSeed: number;
  dice?: DiceView;
  diceActions?: DiceActions;
  /** The tune a player asked for (`inn_song`), however long ago; what plays now is `song.ts currentSong`. */
  song?: InnSongView;
  /** Newest last; the layer plays each `n` once. */
  cheers: InnCheer[];
  /** docs/121 VW2: the cat petted, a stick thrown; newest last, each `n` played once. */
  moments: InnMoment[];
  guestbook: GuestbookEntry[];
  /** docs/121 VW4: what players have pinned on this inn's noticeboard, oldest first. */
  notices: PlayerNotice[];
  deeds: Deed[];
  hooks: Hook[];
  rumours: Rumour[];
  /** The World's week (the noticeboard changes weekly; CI15 feast weeks). */
  week: number;
  /** The season's week, 1–12 (CI15: 1 and 12 are feast weeks). */
  seasonWeek: number;
  /** My heroes who can pay, for the dice stool, a drink, the guestbook. */
  payers: Array<{ id: bigint; key: string; name: string; gold: number }>;
  /** The room upstairs is paid for this visit. */
  rented: boolean;
  /** What a player can ask for that costs coin or lasts. */
  act: {
    toast(): Promise<boolean>;
    drink(heroId: bigint, drink: DrinkKind): Promise<boolean>;
    song(tune: number, heroId: bigint): Promise<boolean>;
    sign(heroId: bigint, text: string): Promise<boolean>;
    rent(heroId: bigint): Promise<boolean>;
    /** docs/121 VW2: tell every view in the room that my hero pets the cat or throws a stick from (`x`, `y`). */
    moment(kind: MomentKind, heroId: bigint, x: number, y: number): Promise<boolean>;
    /** docs/121 VW4: pin a note on the board signed by one of my heroes, or take one of mine down (false: why not). */
    pin(heroId: bigint, title: string, text: string, paper: NotePaper): Promise<string | true>;
    unpin(id: bigint): Promise<boolean>;
    /** docs/121 VW5: take up a side quest from its notice (the band is asked, docs/120 VM6). */
    takeUp?(offerId: bigint): Promise<boolean>;
    /** Go up to the room (rented) or back down to the inn. */
    upstairs(up: boolean): void;
  };
}

export const SONG_TIP = 1;
export const ROOM_PRICE = 5;
